import React, { createContext, useContext, useState, useEffect } from 'react';
import { API_BASE } from '../constants/urls';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Helper function to check URL parameters (for backward compatibility)
const hasTestParam = (param) => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.has(param);
};

// Hook to get current user role
export const useRole = () => {
  const { isAuthenticated, user } = useAuth();

  const getCurrentRole = () => {
    // If user is authenticated, use their actual role from backend
    if (isAuthenticated && user && user.role) {
      return user.role.toLowerCase();
    }

    // Check for TE parameter for backward compatibility
    if (hasTestParam('te')) {
      return 'te';
    }

    // Check for admin parameter for backward compatibility (non-authenticated)
    if (hasTestParam('admin')) {
      return 'admin';
    }

    return 'default';
  };

  const role = getCurrentRole();

  return {
    role,
    isAdmin: role === 'admin' || role === 'superadmin',
    isTe: role === 'te',
    isCoordinator: role === 'coordinator',
    isCe: role === 'ce',
    isDefault: role === 'default',
    isAuthenticated: isAuthenticated
  };
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check authentication status on app load
  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/auth/check`, {
        method: 'GET',
        credentials: 'include',
      });

      const data = await response.json();

      if (response.ok && data.authenticated) {
        setUser(data.user);
        setIsAuthenticated(true);
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (credentials) => {
    try {
      const response = await fetch(`${API_BASE}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(credentials),
      });

      const data = await response.json();

      if (response.ok) {
        setUser(data.user);
        setIsAuthenticated(true);
        return { success: true, user: data.user };
      } else {
        return { success: false, error: data.error || 'Login failed' };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  const logout = async () => {
    try {
      await fetch(`${API_BASE}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  const value = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    checkAuthStatus,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
